import React, { useEffect, useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { FilterChip } from '../FilterChip';
export interface FilterOption {
  id: string;
  label: string;
  value: string;
}
export interface Filter {
  id: string;
  label: string;
  options: FilterOption[];
  selectedValue?: string;
  selectedValues?: string[];
  multiSelect?: boolean;
  type?: 'dropdown' | 'search';
  isOpen?: boolean;
  customDateRange?: {
    startDate: string;
    endDate: string;
  };
  /** Optional heading the filter is listed under in the Add Filter menu */
  group?: string;
}
export interface TableFilterProps {
  filters?: Filter[];
  onFilterChange?: (filterId: string, value: string | string[]) => void;
  onFilterRemove?: (filterId: string) => void;
  onAddFilter?: (filterId: string) => void;
  onToggleDropdown?: (filterId: string) => void;
  onCustomDateChange?: (filterId: string, startDate: string, endDate: string) => void;
  addedFilterIds?: Set<string>;
  'data-id'?: string;
  // New props for granular rendering
  renderOnlyButton?: boolean;
  renderOnlyChips?: boolean;
}
export function TableFilter({
  filters = [],
  onFilterChange,
  onFilterRemove,
  onAddFilter,
  onToggleDropdown,
  onCustomDateChange,
  addedFilterIds = new Set(),
  'data-id': dataId,
  renderOnlyButton,
  renderOnlyChips
}: TableFilterProps) {
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const addFilterRef = useRef<HTMLDivElement>(null);
  // Close add filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addFilterRef.current && !addFilterRef.current.contains(event.target as Node)) {
        setIsAddFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const activeFilters = filters.filter(f => addedFilterIds.has(f.id));
  const availableFilters = filters.filter(f => !addedFilterIds.has(f.id));
  // Group by heading, keeping the order headings first appear in
  const isGrouped = availableFilters.some(f => f.group);
  const filterGroups = availableFilters.reduce<{
    heading?: string;
    filters: Filter[];
  }[]>((groups, filter) => {
    const existing = groups.find(group => group.heading === filter.group);
    if (existing) existing.filters.push(filter);else groups.push({
      heading: filter.group,
      filters: [filter]
    });
    return groups;
  }, []);
  const renderAddButton = () => {
    if (availableFilters.length === 0) return null;
    return <div className="relative" ref={addFilterRef} data-id="element-5818" data-mp="3gzivh6vtxrip">
        <Button variant="outline" size="md" onClick={() => setIsAddFilterOpen(!isAddFilterOpen)} iconLeft={<Plus className="w-4 h-4" data-id="element-5820" data-mp="w7ytd8wdu1ir" />} className={isAddFilterOpen ? 'bg-[#F8FAFC] ring-2 ring-[#E0F2FE] border-[#0EA5E9]' : ''} data-id="element-5819" data-mp="3kz9s3r81f1v5">
          Add Filter
        </Button>

        {isAddFilterOpen && <div className={`absolute top-full mt-2 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-[1000] overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${isGrouped ? 'right-0 w-64 max-h-[420px] overflow-y-auto' : 'left-0 w-48'}`} data-id="element-5821" data-mp="2krctjrbnvtn9">
            <div className="p-1" data-id="element-5822" data-mp="2bwoq86pncm13">
              {filterGroups.map(group => <div key={group.heading ?? 'ungrouped'} className={group.heading ? 'pt-1' : ''} data-id="element-5823" data-mp="2707xt0bezli7">
                  {group.heading && <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5824" data-mp="2214t5gzcjhar">
                      {group.heading}
                    </div>}
                  {group.filters.map(filter => <button key={filter.id} onClick={() => {
              onAddFilter?.(filter.id);
              setIsAddFilterOpen(false);
            }} className="w-full text-left px-3 py-2.5 text-[14px] font-display text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-5825" data-mp="1t06xpn2z4i3s">
                      {filter.label}
                    </button>)}
                </div>)}
            </div>
          </div>}
      </div>;
  };
  const renderChips = () => {
    return activeFilters.map(filter => {
      return <FilterChip key={filter.id} filter={filter} onSelect={value => onFilterChange?.(filter.id, value)} onRemove={() => onFilterRemove?.(filter.id)} onCustomDateChange={onCustomDateChange} data-id="element-5826" data-mp="2k1zgq6rfxrfy" />;
    });
  };
  if (renderOnlyButton) {
    return <div className="relative" data-id="element-5827" data-mp="1wdgarxvv5qr1">{renderAddButton()}</div>;
  }
  if (renderOnlyChips) {
    return <div className="flex items-center gap-3 flex-wrap" data-id="element-5828" data-mp="213wo7hzopcde">{renderChips()}</div>;
  }
  return <div className="flex flex-col gap-4 w-full" data-id="element-5829" data-mp="215raazmozaj2">
      {/* Top Row: Add Filter Button */}
      <div className="flex items-center" data-id="element-5830" data-mp="1naf51yawu1nw">{renderAddButton()}</div>

      {/* Bottom Row: Active Filter Chips */}
      {activeFilters.length > 0 && <div className="flex items-center gap-3 flex-wrap" data-id="element-5831" data-mp="1ddsmjg623mp1">{renderChips()}</div>}
    </div>;
}